/**
 * A hash router, one hash per page, no dependency. The hash keeps every
 * route on the one HTML document the proxy serves, so a refresh on any page
 * works without server-side rewrites.
 */

import { useEffect, useState } from 'react';

export type Route =
  | { page: 'overview' }
  | { page: 'tenants' }
  | { page: 'tenant'; slug: string }
  | { page: 'domain'; slug: string; domain: string }
  | { page: 'mailbox'; slug: string; email: string }
  | { page: 'audit'; tenant?: string }
  | { page: 'nodes' }
  | { page: 'spam' }
  | { page: 'ai' }
  | { page: 'operators' }
  | { page: 'tenant-spam'; slug: string }
  | { page: 'node'; id: string };

export function parse(hash: string): Route {
  const parts = hash.replace(/^#\/?/, '').split('/').filter(Boolean).map(decodeURIComponent);
  if (parts[0] === 'tenants' && parts[1] && parts[2] === 'spam') {
    return { page: 'tenant-spam', slug: parts[1] };
  }
  if (parts[0] === 'spam') {
    return { page: 'spam' };
  }
  if (parts[0] === 'ai') {
    return { page: 'ai' };
  }
  if (parts[0] === 'operators') {
    return { page: 'operators' };
  }
  if (parts[0] === 'tenants' && parts[1] && parts[2] === 'mailboxes' && parts[3]) {
    return { page: 'mailbox', slug: parts[1], email: parts[3] };
  }
  if (parts[0] === 'tenants' && parts[1] && parts[2] === 'domains' && parts[3]) {
    return { page: 'domain', slug: parts[1], domain: parts[3] };
  }
  if (parts[0] === 'tenants' && parts[1]) {
    return { page: 'tenant', slug: parts[1] };
  }
  if (parts[0] === 'tenants') {
    return { page: 'tenants' };
  }
  if (parts[0] === 'nodes' && parts[1]) {
    return { page: 'node', id: parts[1] };
  }
  if (parts[0] === 'nodes') {
    return { page: 'nodes' };
  }
  if (parts[0] === 'audit') {
    return parts[1] ? { page: 'audit', tenant: parts[1] } : { page: 'audit' };
  }
  return { page: 'overview' };
}

export function href(route: Route): string {
  const e = encodeURIComponent;
  switch (route.page) {
    case 'overview':
      return '#/';
    case 'tenants':
      return '#/tenants';
    case 'tenant':
      return `#/tenants/${e(route.slug)}`;
    case 'domain':
      return `#/tenants/${e(route.slug)}/domains/${e(route.domain)}`;
    case 'mailbox':
      return `#/tenants/${e(route.slug)}/mailboxes/${e(route.email)}`;
    case 'audit':
      return route.tenant ? `#/audit/${e(route.tenant)}` : '#/audit';
    case 'nodes':
      return '#/nodes';
    case 'spam':
      return '#/spam';
    case 'ai':
      return '#/ai';
    case 'operators':
      return '#/operators';
    case 'tenant-spam':
      return `#/tenants/${e(route.slug)}/spam`;
    case 'node':
      return `#/nodes/${e(route.id)}`;
  }
}

export function useRoute(): Route {
  const [route, setRoute] = useState(() => parse(window.location.hash));
  useEffect(() => {
    const onChange = () => setRoute(parse(window.location.hash));
    window.addEventListener('hashchange', onChange);
    return () => window.removeEventListener('hashchange', onChange);
  }, []);
  return route;
}
